import { useCallback, useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'

/**
 * Drives Plaid's update-mode relink flow for one linked account.
 *
 * Mounting it fetches an update token and begins loading Plaid, so mount it only for an account
 * that needs relinking. Calling `openRelink` before the token or Plaid is ready queues the request
 * until both are ready.
 */
export default function usePlaidRelink({ accessId, getUpdateLinkToken, onSuccess, onError }) {
  const [updateLinkToken, setUpdateLinkToken] = useState('')
  const [relinkUnavailable, setRelinkUnavailable] = useState(false)
  const [openRequested, setOpenRequested] = useState(false)

  const { open, exit, ready, error } = usePlaidLink({
    onSuccess,
    onExit: err => {
      if (err) {
        console.error(err)
      }
    },
    token: updateLinkToken,
  })

  useEffect(() => {
    if (!accessId) {
      setRelinkUnavailable(true)
      return
    }

    const fetchUpdateLinkToken = async () => {
      try {
        const token = await getUpdateLinkToken(accessId)
        if (!token) {
          throw new Error(`No Plaid update token returned for access id ${accessId}`)
        }
        setUpdateLinkToken(token)
        setRelinkUnavailable(false)
      } catch (err) {
        console.error(err)
        setRelinkUnavailable(true)
      }
    }

    fetchUpdateLinkToken()
  }, [accessId, getUpdateLinkToken])

  useEffect(() => {
    if (error) {
      console.error(error)
      exit()
    }
  }, [error, exit])

  useEffect(() => {
    if (!openRequested) {
      return
    }

    // Plaid will not become ready after reporting an error, so fail any queued request.
    if (relinkUnavailable || error) {
      setOpenRequested(false)
      if (typeof onError === 'function') {
        onError()
      }
      return
    }

    if (!ready) {
      return
    }

    setOpenRequested(false)
    open()
  }, [error, onError, open, openRequested, ready, relinkUnavailable])

  const openRelink = useCallback(() => setOpenRequested(true), [])

  return { openRelink }
}
